import { useEffect, useState, useCallback } from 'react'
import { Book, Plus, X, Trash2, RefreshCw, CheckCircle2 } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Runbook {
  id: number
  title: string
  scenario: string
  body_md: string
  owner: string
  services: string[]
  last_tested_at: string | null
  test_cadence_days: number
  notes: string
  stale_days: number | null
  freshness: 'untested' | 'fresh' | 'stale' | 'overdue'
}

const FRESH_TONE: Record<string, string> = {
  fresh:    'bg-emerald/15 text-emerald border-emerald/30',
  stale:    'bg-amber/15 text-amber-light border-amber/30',
  overdue:  'bg-rose/15 text-rose border-rose/30',
  untested: 'bg-surface-raised text-text-muted border-border',
}

export default function RunbooksPage() {
  const [items, setItems] = useState<Runbook[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [expanded, setExpanded] = useState<number | null>(null)
  const [form, setForm] = useState({ title: '', scenario: '', body_md: '', owner: '', services: '', test_cadence_days: 180, notes: '' })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/res/runbooks')
      setItems(r.data?.runbooks || [])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.title) return
    await api.post('/api/res/runbooks', {
      ...form,
      services: form.services.split(',').map(s => s.trim()).filter(Boolean),
    })
    setShowForm(false)
    setForm({ title: '', scenario: '', body_md: '', owner: '', services: '', test_cadence_days: 180, notes: '' })
    fetchAll()
  }

  const markTested = async (id: number) => {
    await api.post(`/api/res/runbooks/${id}/mark-tested`, {})
    fetchAll()
  }

  const remove = async (id: number) => {
    if (!confirm('Delete runbook?')) return
    await api.delete(`/api/res/runbooks/${id}`)
    fetchAll()
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Book size={24} className="text-blue-light" /> Runbook Library
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Markdown runbooks with last-tested freshness. Overdue ones bubble up.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Add runbook</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total" value={items.length} tone="blue" />
        <Tile label="Fresh" value={items.filter(i => i.freshness === 'fresh').length} tone="emerald" />
        <Tile label="Stale" value={items.filter(i => i.freshness === 'stale').length} tone="amber" pulse={items.some(i => i.freshness === 'stale')} />
        <Tile label="Overdue / untested" value={items.filter(i => i.freshness === 'overdue' || i.freshness === 'untested').length} tone="rose" pulse={items.some(i => i.freshness === 'overdue')} />
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Add runbook</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder='Title e.g. "Ransomware response"' className="input py-1.5 md:col-span-2" />
            <input value={form.scenario} onChange={e => setForm({...form, scenario: e.target.value})} placeholder="Scenario tag e.g. ransomware / outage" className="input py-1.5" />
            <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})} placeholder="Owner" className="input py-1.5" />
            <input value={form.services} onChange={e => setForm({...form, services: e.target.value})} placeholder="Services (comma-separated)" className="input py-1.5 md:col-span-2" />
            <input type="number" value={form.test_cadence_days} onChange={e => setForm({...form, test_cadence_days: parseInt(e.target.value, 10)})} placeholder="Test cadence (days)" className="input py-1.5" />
            <textarea value={form.body_md} onChange={e => setForm({...form, body_md: e.target.value})} rows={6} placeholder="Runbook body (markdown)" className="input py-1.5 md:col-span-2 font-mono text-[11px]" />
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={1} placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {items.length === 0 && (
          <p className="card p-6 text-center text-text-muted text-[11px] italic">No runbooks. Add one above.</p>
        )}
        {items.map(rb => (
          <div key={rb.id} className="card p-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold text-text-primary">{rb.title}</h3>
                  {rb.scenario && <span className="badge text-[9px] bg-surface-raised border border-border text-text-muted">{rb.scenario}</span>}
                  <span className={clsx('badge text-[9px] uppercase border', FRESH_TONE[rb.freshness])}>
                    {rb.freshness}{rb.stale_days != null && ` · ${rb.stale_days}d`}
                  </span>
                </div>
                <div className="text-[10px] text-text-muted mt-0.5">
                  {rb.owner && <>👤 {rb.owner} · </>}
                  cadence {rb.test_cadence_days}d
                  {rb.services?.length > 0 && <> · {rb.services.join(', ')}</>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setExpanded(expanded === rb.id ? null : rb.id)} className="btn-ghost text-[10px]">
                  {expanded === rb.id ? 'hide' : 'view'}
                </button>
                <button type="button" onClick={() => markTested(rb.id)} className="btn-ghost text-[10px] text-emerald" title="Mark tested now">
                  <CheckCircle2 size={11} /> tested
                </button>
                <button type="button" onClick={() => remove(rb.id)} className="text-text-muted hover:text-rose"><Trash2 size={11} /></button>
              </div>
            </div>
            {expanded === rb.id && rb.body_md && (
              <pre className="mt-2 p-2 bg-surface-raised rounded text-[10px] font-mono text-text-secondary whitespace-pre-wrap max-h-96 overflow-y-auto">
                {rb.body_md}
              </pre>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
